// postAuthRedirect.js
//
// Assignment reminder emails ("Complete Now") deep-link straight into an
// authenticated route (e.g. /adminlist?view=TrainingReview&assignment=<id>).
// Layout.js's isAuthenticated route split drops that path+query the moment
// an unauthenticated click bounces through Login or the invitation signup
// flow, so we stash the intended destination here before that happens and
// consume it once handleRedirection() runs after login/signup completes.

const STORAGE_KEY = 'pendingDeepLink';

// Long enough to cover invitation signup (OTP + set password + 2FA) and a
// slow first login after a Complete Now click; short enough that a stale
// link from days ago doesn't hijack an unrelated later login.
const TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Stashes a target path (e.g. "/adminlist?view=TrainingReview&assignment=abc")
 * to be restored after the next successful login or signup.
 * @param {string} target
 */
export const capturePendingDeepLink = (target) => {
  if (!target) return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ target, expiresAt: Date.now() + TTL_MS })
    );
  } catch (e) {
    // localStorage unavailable (e.g. private mode) - deep link falls back
    // to the normal role-based redirect instead of crashing the app.
  }
};

/**
 * Reads and clears the pending deep link, returning it only if still fresh.
 * @returns {string|null}
 */
export const consumePendingDeepLink = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    localStorage.removeItem(STORAGE_KEY);
    const { target, expiresAt } = JSON.parse(raw);
    if (!target || !expiresAt || Date.now() > expiresAt) return null;
    return target;
  } catch (e) {
    return null;
  }
};

export const clearPendingDeepLink = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // no-op
  }
};

/**
 * localStorage.clear() that keeps a pending Complete Now deep link intact.
 * Invitation signup / session-expiry paths call clear() mid-flow; without
 * this the stashed assignment URL is wiped before handleRedirection runs.
 */
export const clearStoragePreservingDeepLink = () => {
  let pending = null;
  try {
    pending = localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
  localStorage.clear();
  if (pending) {
    try {
      localStorage.setItem(STORAGE_KEY, pending);
    } catch (e) {
      // ignore
    }
  }
};
